import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import marketplaceApi from '../marketplaceApi';

const PIPELINE = [
  { key: 'new',         label: 'New',         cls: 'bg-primary'  },
  { key: 'contacted',   label: 'Contacted',   cls: 'bg-info'     },
  { key: 'no_response', label: 'No Response', cls: 'bg-warning'  },
  { key: 'converted',   label: 'Converted',   cls: 'bg-success'  },
  { key: 'lost',        label: 'Lost',        cls: 'bg-danger'   },
];

export default function MarketplaceDashboard() {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    marketplaceApi.get('/stats')
      .then(r => setStats(r.data.stats))
      .catch(() => setError('Failed to load stats.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="content-wrapper">
      <section className="content pt-4">
        <div className="container-fluid text-center py-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      </section>
    </div>
  );

  if (error || !stats) return (
    <div className="content-wrapper">
      <section className="content pt-4">
        <div className="container-fluid">
          <div className="alert alert-danger">{error || 'Failed to load.'}</div>
        </div>
      </section>
    </div>
  );

  const leads       = stats.leads  || {};
  const clicks      = stats.clicks || {};
  const rating      = stats.rating;
  const totalLeads  = leads.total || 0;
  const converted   = leads.converted || 0;
  const convRate    = totalLeads > 0 ? ((converted / totalLeads) * 100).toFixed(1) : '0';

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <h1 className="m-0"><i className="fas fa-globe mr-2 text-success" />Marketplace Dashboard</h1>
        </div>
      </div>
      <section className="content">
        <div className="container-fluid">

          {/* Stats row */}
          <div className="row">
            {[
              { label: 'Active Listings', value: stats.active_listings || 0, icon: 'fa-motorcycle', color: 'success' },
              { label: 'New Leads',       value: leads.new || 0,              icon: 'fa-bell',       color: 'warning', sub: `${leads.today || 0} today` },
              { label: 'Total Views',     value: clicks.view || 0,            icon: 'fa-eye',        color: 'info',    sub: `${clicks.today || 0} today` },
              { label: 'Conversion Rate', value: `${convRate}%`,              icon: 'fa-chart-line', color: 'primary', sub: `${converted} deals closed` },
              { label: "Today's Leads",   value: leads.today || 0,            icon: 'fa-calendar-day',color:'danger'  },
              ...(rating?.average ? [{ label: 'Avg Rating', value: `${Number(rating.average).toFixed(1)} ★`, icon: 'fa-star', color: 'warning', sub: `${rating.total} reviews` }] : []),
            ].map(({ label, value, icon, color, sub }) => (
              <div className="col-lg-2 col-md-4 col-sm-6" key={label}>
                <div className={`small-box bg-${color}`}>
                  <div className="inner">
                    <h3>{value}</h3>
                    <p>{label}{sub ? <><br /><small>{sub}</small></> : null}</p>
                  </div>
                  <div className="icon"><i className={`fas ${icon}`} /></div>
                </div>
              </div>
            ))}
          </div>

          <div className="row">
            {/* Lead Pipeline */}
            <div className="col-md-7">
              <div className="card card-outline card-primary">
                <div className="card-header">
                  <h3 className="card-title"><i className="fas fa-funnel-dollar mr-2" />Lead Pipeline</h3>
                </div>
                <div className="card-body">
                  {totalLeads === 0 ? (
                    <p className="text-muted mb-0">No leads yet. Leads appear when buyers enquire about your vehicles.</p>
                  ) : PIPELINE.map(({ key, label, cls }) => {
                    const count = leads[key] || 0;
                    const pct   = Math.round((count / totalLeads) * 100);
                    return (
                      <div key={key} className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <small className="font-weight-bold">{label}</small>
                          <small className="text-muted">{count} ({pct}%)</small>
                        </div>
                        <div className="progress progress-sm">
                          <div className={`progress-bar ${cls}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="col-md-5">
              <div className="card card-outline card-success">
                <div className="card-header">
                  <h3 className="card-title"><i className="fas fa-bolt mr-2" />Quick Actions</h3>
                </div>
                <div className="card-body">
                  <div className="d-grid gap-2">
                    <Link to="/marketplace/leads?status=new" className="btn btn-warning btn-block mb-2">
                      <i className="fas fa-bell mr-2" />New Leads ({leads.new || 0})
                    </Link>
                    <Link to="/marketplace/listings" className="btn btn-success btn-block mb-2">
                      <i className="fas fa-list mr-2" />My Listings ({stats.active_listings || 0})
                    </Link>
                    <Link to="/marketplace/leads" className="btn btn-outline-secondary btn-block mb-2">
                      <i className="fas fa-users mr-2" />All Leads ({totalLeads})
                    </Link>
                    <Link to="/marketplace/analytics" className="btn btn-outline-info btn-block mb-2">
                      <i className="fas fa-chart-line mr-2" />View Analytics
                    </Link>
                    {rating?.total > 0 && (
                      <Link to="/marketplace/reviews" className="btn btn-outline-warning btn-block">
                        <i className="fas fa-star mr-2" />Reviews ({rating.total})
                      </Link>
                    )}
                  </div>
                </div>
              </div>

              <div className="card card-outline card-secondary">
                <div className="card-header"><h3 className="card-title">How leads work</h3></div>
                <div className="card-body p-2">
                  {[
                    { dot: 'primary', text: 'New — buyer enquired about one of your vehicles' },
                    { dot: 'info',    text: 'Contacted — you reached out to the buyer' },
                    { dot: 'warning', text: 'No Response — buyer didn\'t reply' },
                    { dot: 'success', text: 'Converted — deal done!' },
                  ].map(({ dot, text }) => (
                    <p key={text} className="mb-1 small">
                      <span className={`badge badge-${dot} mr-2`}>&nbsp;</span>{text}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}
